/**
 * Ensures a URL has an https:// protocol prefix.
 * If the URL already has http:// or https://, it returns the URL unchanged.
 * If the URL has no protocol, https:// is prepended.
 *
 * @param url - The URL string to normalize
 * @returns The URL with https:// protocol
 *
 * @example
 * ```typescript
 * ensureHttps("example.com");           // "https://example.com"
 * ensureHttps("www.example.com");       // "https://www.example.com"
 * ensureHttps("https://example.com");   // "https://example.com"
 * ensureHttps("http://example.com");    // "http://example.com"
 * ensureHttps("");                      // ""
 * ```
 */
export const toYouTubeEmbedUrl = (url: string): string | null => {
  if (!url || !url.trim()) return null;

  let parsed: URL;
  try {
    parsed = new URL(ensureHttps(url));
  } catch {
    return null;
  }

  const hostname = parsed.hostname.replace(/^www\./, "");

  let videoId: string | null = null;

  if (hostname === "youtube.com") {
    if (parsed.pathname.startsWith("/embed/")) {
      videoId = parsed.pathname.slice("/embed/".length);
    } else {
      videoId = parsed.searchParams.get("v");
    }
  } else if (hostname === "youtu.be") {
    videoId = parsed.pathname.slice(1);
  }

  if (!videoId) return null;

  return `https://www.youtube.com/embed/${videoId}`;
};

export function ensureHttps(url: string): string {
  if (!url) return url;

  const trimmed = url.trim();
  if (!trimmed) return trimmed;

  // Already has a protocol
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
    return trimmed;
  }

  // Prepend https://
  return `https://${trimmed}`;
}
